import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Divider, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { DayOfWeek, UpdatePricelistDto } from '../../../../common/PricePrgClient';
import { ProgressButton } from '../../../../common/ProgressButton';
import { WeekdayChooser } from '../../../../common/WeekdayChooser';
import { FormCheckbox, FormTextField } from '../../../../common/form';
import { RootState } from '../../../../store';
import { getPricelistSettings } from '../../../../utils';
import { getAppSettings } from '../../../settings/redux/settingsSelectors';
import { getCurrentUserPermissions } from '../../../user/redux/userSelectors';
import {
    getPricelists,
    getSelectedPricelistPermissions,
    getSelectedPricelists
} from '../../redux/pricelistSelectors';
import { updatePricelist } from '../../redux/pricelistSlices';

const mapStateToProps = (state: RootState) => {
    const pricelistPermissions = getSelectedPricelistPermissions(state);
    return {
        selectedPricelist: getSelectedPricelists(state)[0],
        hasWritePermission: pricelistPermissions.length > 0 ? pricelistPermissions[0].canUpdate : false,
        pricelists: getPricelists(state),
        userPermissions: getCurrentUserPermissions(state),
        appSettings: getAppSettings(state)
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const GeneralPricelistTab = ({
    selectedPricelist,
    hasWritePermission,
    pricelists,
    userPermissions,
    appSettings: { defaultCurrency, availableCurrencies }
}: Props) => {
    const schema: yup.SchemaOf<UpdatePricelistDto> = yup.object().shape({
        name: yup.string().max(40).required().label('Name'),
        isBasePricelist: yup.boolean().required().label('Is base pricelist'),
        applyCommentsToPricelist: yup.boolean().required().label('Apply comments to pricelist'),
        emptyPricelistOnCreate: yup.boolean().required().label('Empty pricelist on create'),
        pricelistPeriodDayLength: yup.number().required().label('Pricelist period day length'),
        currency: yup.string().max(3).min(3).oneOf(availableCurrencies).required().label('Currency'),
        isReadonly: yup.boolean().label('isReadOnly').required(),
        hasWeekdayRestrictions: yup.boolean().required().label('Has weekday restrictions'),
        allowedWeekdays: yup
            .array(
                yup
                    .mixed<DayOfWeek>()
                    .oneOf([
                        DayOfWeek.Monday,
                        DayOfWeek.Tuesday,
                        DayOfWeek.Wednesday,
                        DayOfWeek.Thursday,
                        DayOfWeek.Friday,
                        DayOfWeek.Saturday,
                        DayOfWeek.Sunday
                    ])
                    .required()
            )
            .optional()
            .default([])
            .label('Allowed weekdays'),
        forbidCalculationBypass: yup.boolean().required().label('Forbid bypass of calculations'),
        ignoreAgreementPricesOnPricelistMail: yup
            .boolean()
            .required()
            .label('Ignore agreement prices on pricelist mail'),
        useDefaultCurrencyConversion: yup.boolean().required().label('Use default currency conversion')
    });

    const pricelistSettings = useMemo(
        () => getPricelistSettings(selectedPricelist, pricelists),
        [selectedPricelist, pricelists]
    );

    const mainPricelist = pricelistSettings?.mainCurrencyPricelistId
        ? pricelists[pricelistSettings.mainCurrencyPricelistId]
        : null;

    const defaultValues = useMemo<Partial<UpdatePricelistDto>>(
        () => ({
            allowedWeekdays: pricelistSettings?.allowedWeekdays ?? [],
            applyCommentsToPricelist: pricelistSettings?.applyCommentsToChildren ?? false,
            currency: selectedPricelist?.currency ?? defaultCurrency ?? '',
            emptyPricelistOnCreate: pricelistSettings?.emptyPricelistOnCreate ?? false,
            hasWeekdayRestrictions: pricelistSettings?.hasWeekdayRestrictions ?? false,
            isBasePricelist: pricelistSettings?.isBasePricelist ?? false,
            name: selectedPricelist?.name ?? '',
            pricelistPeriodDayLength: pricelistSettings?.pricelistPeriodDayLength ?? 365,
            forbidCalculationBypass: pricelistSettings?.forbidCalculationBypass ?? false,
            isReadonly: pricelistSettings?.isReadonly ?? false,
            ignoreAgreementPricesOnPricelistMail:
                pricelistSettings?.ignoreAgreementPricesOnPricelistMail ?? false,
            useDefaultCurrencyConversion: pricelistSettings?.useDefaultCurrencyConversion ?? false
        }),
        [defaultCurrency, selectedPricelist, pricelistSettings]
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty },
        getValues
    } = useForm<UpdatePricelistDto>({
        resolver: yupResolver(schema),
        defaultValues
    });

    const dispatch = useDispatch();

    useEffect(() => {
        reset(defaultValues);
    }, [selectedPricelist, reset, defaultValues]);

    if (selectedPricelist == null) {
        return (
            <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 400, marginTop: '5%' }}>
                Choose a pricelist to view more details about it.
            </Typography>
        );
    }

    const submit = (form: UpdatePricelistDto) => {
        dispatch(updatePricelist.request({ pricelistId: selectedPricelist.pricelistId, dto: form }));
    };

    const isChildPricelist =
        selectedPricelist.parentPricelistId != null || selectedPricelist.mainCurrencyPricelistId != null;

    const PricelistDivider = () => (
        <Grid container direction="row">
            <Grid item xs={12} md={12} lg={7}>
                <Divider sx={{ margin: '16px 0' }} orientation="horizontal" variant="fullWidth" />
            </Grid>
        </Grid>
    );

    return (
        <>
            <Typography variant="h5">{selectedPricelist.name}</Typography>
            <PricelistDivider />

            <form id="pricelistForm" onSubmit={handleSubmit(submit)}>
                <Grid item xs={12} md={12} lg={7}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        sx={theme => ({
                            [theme.breakpoints.down('xl')]: {
                                flexDirection: 'column'
                            }
                        })}
                        width="100%"
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            sx={theme => ({
                                [theme.breakpoints.down('xl')]: {
                                    order: 2
                                }
                            })}
                            flexBasis="50%"
                        >
                            <FormCheckbox
                                control={control}
                                name="isBasePricelist"
                                label="Base pricelist"
                                disabled={!hasWritePermission || selectedPricelist.parentPricelistId != null}
                            />
                            <FormCheckbox
                                control={control}
                                name="applyCommentsToPricelist"
                                label="Apply comments to pricelist children"
                                disabled={!hasWritePermission}
                            />
                            <FormCheckbox
                                control={control}
                                name="emptyPricelistOnCreate"
                                label="Create empty pricelists"
                                disabled={!hasWritePermission || isChildPricelist}
                            />
                            <Tooltip title="Check this to forbid prices to be set on this pricelist which has not been calculated through its calculation">
                                <span>
                                    <FormCheckbox
                                        control={control}
                                        name="forbidCalculationBypass"
                                        label="Forbid bypass of calculations"
                                        disabled={!hasWritePermission}
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip title="Check this to exclude agreement prices on pricelist mails">
                                <span>
                                    <FormCheckbox
                                        control={control}
                                        name="ignoreAgreementPricesOnPricelistMail"
                                        label="Exclude agreement prices on pricelist mail"
                                        disabled={
                                            !hasWritePermission || selectedPricelist.parentPricelistId != null
                                        }
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip title="Check this to enable exchange rate conversion">
                                <span>
                                    <FormCheckbox
                                        control={control}
                                        name="useDefaultCurrencyConversion"
                                        label="Use currency conversion"
                                        disabled={
                                            !hasWritePermission || selectedPricelist.parentPricelistId != null
                                        }
                                    />
                                </span>
                            </Tooltip>
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                            sx={theme => ({
                                flexBasis: 300,
                                [theme.breakpoints.down('xl')]: {
                                    flexBasis: 'auto'
                                }
                            })}
                        >
                            <FormTextField
                                control={control}
                                label="Name"
                                type="text"
                                name="name"
                                disabled={!hasWritePermission}
                                fullWidth
                                variant="standard"
                            />
                            <FormTextField
                                control={control}
                                label="Pricelist period length (Days)"
                                type="number"
                                variant="standard"
                                name="pricelistPeriodDayLength"
                                sx={{
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                        {
                                            '-webkit-appearance': 'none',
                                            margin: 0
                                        }
                                }}
                                disabled={!hasWritePermission || isChildPricelist}
                                style={{ whiteSpace: 'nowrap' }}
                                fullWidth
                            />
                            <FormTextField
                                control={control}
                                name="currency"
                                label="Currency"
                                type="text"
                                disabled={true}
                                style={{ whiteSpace: 'nowrap' }}
                                fullWidth
                                variant="standard"
                            />
                            <TextField
                                disabled={true}
                                label="Main currency pricelist"
                                value={
                                    mainPricelist != null
                                        ? `${mainPricelist.pricelistRef} - ${mainPricelist.name}`
                                        : 'None'
                                }
                                variant="standard"
                            />
                        </Box>
                    </Box>
                </Grid>
                <PricelistDivider />
                <Grid container direction="row">
                    <Grid item xs={12} md={12} lg={6}>
                        <FormCheckbox
                            control={control}
                            name="hasWeekdayRestrictions"
                            label="Has weekday restrictions"
                            disabled={!hasWritePermission || isChildPricelist}
                        />
                    </Grid>
                    <Grid item xs={12} md={12} lg={4}>
                        <FormCheckbox
                            control={control}
                            name="isReadonly"
                            label="Read only"
                            disabled={!userPermissions.isAdmin || isChildPricelist}
                        />
                    </Grid>
                </Grid>
                <Grid container direction="row">
                    <Grid item xs={12} md={12} lg={6}>
                        <Controller
                            name="allowedWeekdays"
                            control={control}
                            render={({ field: { onChange, value } }) => {
                                return (
                                    <WeekdayChooser
                                        selectedWeekdays={value}
                                        disabled={!getValues('hasWeekdayRestrictions') || isChildPricelist}
                                        setSelectedWeekdays={onChange}
                                    />
                                );
                            }}
                        />
                    </Grid>
                </Grid>
                <PricelistDivider />
                <Grid item>
                    <ProgressButton
                        variant="contained"
                        color="primary"
                        type="submit"
                        form="pricelistForm"
                        isLoading={false}
                        disabled={!isDirty || !hasWritePermission}
                    >
                        Save
                    </ProgressButton>
                </Grid>
            </form>
        </>
    );
};

export default connect(mapStateToProps)(GeneralPricelistTab);
